import React from 'react'
import { Button, Card, CardContent, CardHeader, Checkbox, Label } from '@/shared/ui'
import { listPlansWithPolicy, upsertPlanPolicy, PlanWithPolicy } from '../services/planPushPolicyService'
import { getSupportedTimeframes } from '../services/planPushPolicyService'
import { getRolePushPolicy, upsertRolePushPolicy } from '../services/rolePushPolicyService'
import { useToast } from '@/shared/ui/Toast'

export default function PlanPushPolicySettings() {
  const { add: toast } = useToast()
  const [rows, setRows] = React.useState<PlanWithPolicy[] | null>(null)
  const [aff, setAff] = React.useState<{ push_enabled: boolean; allowed_timeframes: string[] } | null>(null)
  const [saving, setSaving] = React.useState<string | null>(null)
  const [savingAff, setSavingAff] = React.useState<boolean>(false)

  React.useEffect(()=>{
    let alive = true
    ;(async()=>{
      try {
        const [list, role] = await Promise.all([
          listPlansWithPolicy(),
          getRolePushPolicy('afiliado').catch(()=> ({ role: 'afiliado', push_enabled: false, allowed_timeframes: [] })),
        ])
        if (alive) {
          setRows(list)
          setAff({ push_enabled: role.push_enabled, allowed_timeframes: role.allowed_timeframes })
        }
      } catch (e:any) {
        toast({ title: 'Planos & Push', description: e?.message || 'Falha ao carregar', variant: 'error' })
      }
    })()
    return ()=>{ alive=false }
  },[])

  const tfs = getSupportedTimeframes()

  function togglePlan(id: string, field: 'push_enabled' | 'tf', tf?: string) {
    setRows(prev => {
      if (!prev) return prev
      return prev.map(r => {
        if (r.plan_id !== id) return r
        if (field === 'push_enabled') return { ...r, push_enabled: !r.push_enabled }
        if (field === 'tf' && tf) {
          const has = r.allowed_timeframes.includes(tf)
          const next = has ? r.allowed_timeframes.filter(x => x !== tf) : [...r.allowed_timeframes, tf]
          return { ...r, allowed_timeframes: next }
        }
        return r
      })
    })
  }

  function toggleAffiliate(field: 'push_enabled' | 'tf', tf?: string) {
    setAff(prev => {
      if (!prev) return prev
      if (field === 'push_enabled') return { ...prev, push_enabled: !prev.push_enabled }
      if (field === 'tf' && tf) {
        const has = prev.allowed_timeframes.includes(tf)
        const next = has ? prev.allowed_timeframes.filter(x => x !== tf) : [...prev.allowed_timeframes, tf]
        return { ...prev, allowed_timeframes: next }
      }
      return prev
    })
  }

  async function savePlan(p: PlanWithPolicy) {
    if (saving) return
    setSaving(p.plan_id)
    try {
      await upsertPlanPolicy(p.plan_id, p.push_enabled, p.allowed_timeframes)
      toast({ title: 'Planos & Push', description: 'Política salva para '+p.name, variant: 'success', soft: true })
    } catch (e:any) {
      toast({ title: 'Planos & Push', description: e?.message || 'Falha ao salvar', variant: 'error' })
    } finally {
      setSaving(null)
    }
  }

  async function saveAffiliate() {
    if (!aff || savingAff) return
    setSavingAff(true)
    try {
      await upsertRolePushPolicy('afiliado', aff.push_enabled, aff.allowed_timeframes)
      toast({ title: 'Planos & Push', description: 'Política salva para Afiliado', variant: 'success', soft: true })
    } catch (e:any) {
      toast({ title: 'Planos & Push', description: e?.message || 'Falha ao salvar', variant: 'error' })
    } finally { setSavingAff(false) }
  }

  if (!rows || !aff) return (
    <Card><CardHeader className="text-sm font-medium">Planos e Timeframes (Push)</CardHeader><CardContent><div className="text-sm text-foreground/70">Carregando…</div></CardContent></Card>
  )

  return (
    <Card>
      <CardHeader className="text-sm font-medium">Planos e Timeframes (Push)</CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="rounded-md border border-border p-3 bg-muted/30">
            <div className="mb-2 flex items-center justify-between">
              <div className="font-medium">Afiliado</div>
              <div className="flex items-center gap-2">
                <Label>Push desativado</Label>
                <input type="checkbox" className="scale-110" checked={aff.push_enabled} onChange={()=> toggleAffiliate('push_enabled')} />
                <Label>Push ativado</Label>
              </div>
            </div>
            <div className="text-sm text-foreground/70 mb-2">Timeframes permitidos (aplicado somente quando push estiver ativado para afiliado):</div>
            <div className="flex flex-wrap gap-4">
              {tfs.map((tf)=> (
                <label key={tf} className="inline-flex items-center gap-2">
                  <Checkbox checked={aff.allowed_timeframes.includes(tf)} onChange={()=> toggleAffiliate('tf', tf)} />
                  <span className="text-sm">{tf}</span>
                </label>
              ))}
            </div>
            <div className="mt-3">
              <Button disabled={savingAff} onClick={saveAffiliate}>Salvar</Button>
            </div>
          </div>
          {rows.map((p)=> (
            <div key={p.plan_id} className="rounded-md border border-border p-3">
              <div className="mb-2 flex items-center justify-between">
                <div className="font-medium">{p.name}</div>
                <div className="flex items-center gap-2">
                  <Label>Push desativado</Label>
                  <input type="checkbox" className="scale-110" checked={p.push_enabled} onChange={()=> togglePlan(p.plan_id, 'push_enabled')} />
                  <Label>Push ativado</Label>
                </div>
              </div>
              <div className="text-sm text-foreground/70 mb-2">Timeframes permitidos (aplicado somente quando o plano tem push ativado):</div>
              <div className="flex flex-wrap gap-4">
                {tfs.map((tf)=> (
                  <label key={tf} className="inline-flex items-center gap-2">
                    <Checkbox checked={p.allowed_timeframes.includes(tf)} onChange={()=> togglePlan(p.plan_id, 'tf', tf)} />
                    <span className="text-sm">{tf}</span>
                  </label>
                ))}
              </div>
              <div className="mt-3">
                <Button disabled={saving===p.plan_id} onClick={()=> savePlan(p)}>Salvar</Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
